'use client';

import { PropsWithChildren, ReactNode } from 'react';

import * as Dialog from '@radix-ui/react-dialog';
import clsx from 'clsx';

import { Button } from '../Button';
import { Close } from '../Icons';

import styles from './Modal.module.scss';

type ModalProps = PropsWithChildren<{
  /*
   * The header of the modal.
   */
  header: ReactNode;
  /*
   * Controls the modal visibility.
   */
  isOpened: boolean;
  /*
   * The callback to handle the modal close.
   */
  onClose?: () => void;
  /*
   * The additional class names for the dialog-content div.
   */
  className?: string;

  /*
   * The additional class names for the close button.
   */
  closeButtonClassName?: string;

  /*
   * The aria-label for the close button.
   */
  ariaCloseLabel?: string;

  /*
   * If true, the modal will take the full width of the screen.
   */
  fullWidth?: boolean;
}>;

export const Modal = ({
  header,
  children,
  isOpened,
  onClose,
  className,
  ariaCloseLabel = 'Close',
  closeButtonClassName,
  fullWidth,
}: ModalProps) => {
  return (
    <Dialog.Root open={isOpened}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles['dialog-overlay']} />
        <Dialog.Content
          className={clsx(styles['dialog-content'], { [styles['dialog-content-full-width']]: fullWidth }, className)}
          onPointerDownOutside={onClose}
          onEscapeKeyDown={onClose}
        >
          <Dialog.Title className={styles['dialog-title']}>
            {header}
            <Dialog.Close asChild>
              <Button
                onClick={onClose}
                aria-label={ariaCloseLabel}
                className={clsx(styles['close-button'], closeButtonClassName)}
                variant="tonal"
              >
                <Close size={14} />
              </Button>
            </Dialog.Close>
          </Dialog.Title>
          <div className={styles['dialog-children']}>{children}</div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
